/**
 * Accounting Entry API
 *
 * RTK Query API for Accounting Entry operations.
 * Provides hooks for CRUD operations on accounting entries.
 */

import { baseApi } from './baseApi';
import type {
  CreateAccountingEntryRequest,
  UpdateAccountingEntryRequest,
  AccountingEntryResponse,
  AccountingEntryListResponse,
  DeleteAccountingEntryResponse,
} from '@OrianaTypes';

interface ApiResponse<T> {
  success: boolean;
  data: T;
  pagination?: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export const accountingApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    /**
     * GET /accounting/po/{poId}
     * Fetch all accounting entries for a PO
     */
    getAccountingEntriesByPoId: builder.query<
      AccountingEntryResponse[],
      { poId: string; moduleType?: string }
    >({
      query: ({ poId, moduleType }) => ({
        url: `/accounting/po/${poId}`,
        params: moduleType ? { moduleType } : {},
      }),
      transformResponse: (response: ApiResponse<AccountingEntryResponse[]>) => response.data,
      providesTags: (result, _error, { poId }) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: 'AccountingEntry' as const, id })),
              { type: 'AccountingEntry', id: `PO_${poId}` },
            ]
          : [{ type: 'AccountingEntry', id: `PO_${poId}` }],
    }),

    /**
     * GET /accounting/{id}
     * Fetch a single accounting entry by ID
     */
    getAccountingEntryById: builder.query<AccountingEntryResponse, number>({
      query: (id) => `/accounting/${id}`,
      transformResponse: (response: ApiResponse<AccountingEntryResponse>) => response.data,
      providesTags: (_result, _error, id) => [{ type: 'AccountingEntry', id }],
    }),

    /**
     * POST /accounting
     * Create a new accounting entry
     */
    createAccountingEntry: builder.mutation<
      AccountingEntryResponse,
      CreateAccountingEntryRequest
    >({
      query: (body) => ({
        url: '/accounting',
        method: 'POST',
        body,
      }),
      transformResponse: (response: ApiResponse<AccountingEntryResponse>) => response.data,
      invalidatesTags: (_result, _error, arg) => [
        { type: 'AccountingEntry', id: `PO_${arg.poId}` },
      ],
    }),

    /**
     * PUT /accounting/{id}
     * Update an accounting entry
     */
    updateAccountingEntry: builder.mutation<
      AccountingEntryResponse,
      { id: number; data: UpdateAccountingEntryRequest; poId?: string }
    >({
      query: ({ id, data }) => ({
        url: `/accounting/${id}`,
        method: 'PUT',
        body: data,
      }),
      transformResponse: (response: ApiResponse<AccountingEntryResponse>) => response.data,
      invalidatesTags: (_result, _error, { id, poId }) =>
        poId
          ? [{ type: 'AccountingEntry', id }, { type: 'AccountingEntry', id: `PO_${poId}` }]
          : [{ type: 'AccountingEntry', id }],
    }),

    /**
     * DELETE /accounting/{id}
     * Delete an accounting entry
     */
    deleteAccountingEntry: builder.mutation<
      DeleteAccountingEntryResponse,
      { id: number; poId?: string }
    >({
      query: ({ id }) => ({
        url: `/accounting/${id}`,
        method: 'DELETE',
      }),
      transformResponse: (response: ApiResponse<DeleteAccountingEntryResponse>) => response.data,
      invalidatesTags: (_result, _error, { id, poId }) =>
        poId
          ? [{ type: 'AccountingEntry', id }, { type: 'AccountingEntry', id: `PO_${poId}` }]
          : [{ type: 'AccountingEntry', id }],
    }),
  }),
});

export const {
  useGetAccountingEntriesByPoIdQuery,
  useGetAccountingEntryByIdQuery,
  useCreateAccountingEntryMutation,
  useUpdateAccountingEntryMutation,
  useDeleteAccountingEntryMutation,
} = accountingApi;
